import {
    Box,
    FormControlLabel,
    Switch,
    IconButton,
    CircularProgress,
    useTheme,
    Chip,
    Divider,
    ChipProps,
    TextField,
    InputAdornment,
    Typography,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    useMediaQuery,
    Badge
} from '@mui/material';
import { Refresh, Search, MoreVert, ViewCompact } from '@mui/icons-material';
import React, { useRef, useEffect, useState } from 'react';
import { getToolbarStyles } from '../config/theme';
import { useStoreConfig } from '../contexts/StoreConfigContext';

export interface FilterChipConfig {
    label: string;
    value: string;
    count?: number;
    icon?: React.ReactNode;
    color?: ChipProps['color'];
}

export interface SearchConfig {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
}

interface TableControlBarProps {
    // Dense mode
    dense?: boolean;
    onDenseChange?: (dense: boolean) => void;
    showDenseToggle?: boolean;
    
    // Refresh
    isLoading?: boolean;
    onRefresh?: () => void | Promise<void>;
    showRefresh?: boolean;
    
    // Filters (chip-based)
    filters?: FilterChipConfig[];
    activeFilter?: string;
    onFilterChange?: (value: string) => void;
    filterDescription?: string; // Optional text showing current filter state

    // Search
    search?: SearchConfig;

    // Custom actions (right side, before dense/refresh)
    customActions?: React.ReactNode;

    // Active filter count for mobile badge indicator
    activeFilterCount?: number;
}

/**
 * Standardized control bar for Portal tables
 * Provides refresh, dense mode, search, and dynamic filters in a ribbon layout
 */
export const TableControlBar: React.FC<TableControlBarProps> = ({
    dense = false,
    onDenseChange,
    showDenseToggle = true,
    isLoading = false,
    onRefresh,
    showRefresh = true,
    filters = [],
    activeFilter,
    onFilterChange,
    filterDescription,
    search,
    customActions,
    activeFilterCount = 0
}) => {
    const theme = useTheme();
    const { config } = useStoreConfig();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const filterContainerRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const leftSideRef = useRef<HTMLDivElement>(null);
    const rightSideRef = useRef<HTMLDivElement>(null);
    const [isScrollable, setIsScrollable] = useState(false);
    const [isScrolledFromStart, setIsScrolledFromStart] = useState(false);
    const [filterMaxWidth, setFilterMaxWidth] = useState<number>(600);
    const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
    const mobileMenuOpen = Boolean(mobileMenuAnchor);

    const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMobileMenuAnchor(event.currentTarget);
    };

    const handleMobileMenuClose = () => {
        setMobileMenuAnchor(null);
    };

    const handleMobileDenseToggle = () => {
        onDenseChange?.(!dense);
    };

    const handleMobileRefresh = () => {
        onRefresh?.();
        handleMobileMenuClose();
    };

    // Organize filters: active filter first, then divider, then others
    const organizedFilters = React.useMemo(() => {
        if (!activeFilter) return { active: null, others: filters };
        
        const active = filters.find(f => f.value === activeFilter);
        const others = filters.filter(f => f.value !== activeFilter);
        
        return { active, others };
    }, [filters, activeFilter]);

    const handleDenseChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onDenseChange?.(event.target.checked);
    };

    const handleRefreshClick = () => {
        onRefresh?.();
    };

    // Helper function to render a filter chip
    const renderFilterChip = (filter: FilterChipConfig) => {
        const isActive = activeFilter === filter.value;
        const chipProps = {
            key: filter.value,
            label: filter.count !== undefined ? `${filter.label} (${filter.count})` : filter.label,
            variant: isActive ? 'filled' : 'outlined' as 'filled' | 'outlined',
            color: "default" as const,
            onClick: () => onFilterChange?.(filter.value),
            size: "small" as const,
            sx: {
                fontWeight: isActive ? 600 : 400,
                flexShrink: 0,
                marginTop: 0.5,
                paddingX: 1,
                paddingY: 2,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                ...(isActive && {
                    backgroundColor: config.primaryColor,
                    color: '#fff',
                    borderColor: config.primaryColor,
                    '& .MuiChip-icon': {
                        color: '#fff',
                    },
                    boxShadow: `0 0 0 2px ${config.primaryColor}40`,
                }),
                '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: theme.palette.mode === 'dark'
                        ? '0 2px 8px rgba(0, 0, 0, 0.4)'
                        : '0 2px 8px rgba(0, 0, 0, 0.15)',
                    ...(isActive && {
                        backgroundColor: config.primaryColor,
                    }),
                }
            }
        };

        return filter.icon ? (
            <Chip
                {...chipProps}
                icon={filter.icon as React.ReactElement}
            />
        ) : (
            <Chip {...chipProps} />
        );
    };

    // Calculate dynamic max width for filter container
    useEffect(() => {
        const calculateMaxWidth = () => {
            if (containerRef.current && leftSideRef.current && rightSideRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                const leftSideWidth = leftSideRef.current.offsetWidth;
                const rightSideWidth = rightSideRef.current.offsetWidth;
                
                // Calculate available space: container width - left side - right side - gaps - safety margin
                const gaps = 32; // Account for gaps between sections (2 * 16px)
                const fadeOverlayWidth = 80; // Account for fade overlays (40px each side) + visual spacing
                const safetyMargin = 20; // Additional safety margin
                const availableWidth = containerWidth - leftSideWidth - rightSideWidth - gaps - fadeOverlayWidth - safetyMargin;
                
                // Set a reasonable minimum and maximum
                const minWidth = 400;
                const maxWidth = 800;
                const calculatedWidth = Math.max(minWidth, Math.min(maxWidth, availableWidth));
                
                setFilterMaxWidth(calculatedWidth);
            }
        };

        calculateMaxWidth();
        
        // Use ResizeObserver for more accurate tracking
        const resizeObserver = new ResizeObserver(calculateMaxWidth);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        window.addEventListener('resize', calculateMaxWidth);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', calculateMaxWidth);
        };
    }, [search, customActions, showDenseToggle, showRefresh]); // Re-calculate when these change

    // Check if filter container is scrollable
    useEffect(() => {
        const checkScrollable = () => {
            if (filterContainerRef.current) {
                const { scrollWidth, clientWidth } = filterContainerRef.current;
                setIsScrollable(scrollWidth > clientWidth);
            }
        };

        checkScrollable();
        window.addEventListener('resize', checkScrollable);

        return () => window.removeEventListener('resize', checkScrollable);
    }, [organizedFilters, filterMaxWidth]); // Re-check when filters or maxWidth change

    // Track scroll position for left fade
    useEffect(() => {
        const handleScroll = () => {
            if (filterContainerRef.current) {
                const { scrollLeft } = filterContainerRef.current;
                setIsScrolledFromStart(scrollLeft > 10); // Show left fade after scrolling 10px
            }
        };

        const container = filterContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
        return undefined;
    }, []);

    // Mobile Layout
    if (isMobile) {
        return (
            <Box
                ref={containerRef}
                sx={getToolbarStyles(theme, {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 2,
                    px: 1.5,
                    py: 1,
                })}
            >
                {/* Search Bar - Takes most of the space on mobile */}
                {search && (
                    <TextField
                        size='small'
                        placeholder={search.placeholder || 'Search...'}
                        value={search.value}
                        onChange={(e) => search.onChange(e.target.value)}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                height: 36,
                                fontSize: '0.8125rem',
                                '& input': {
                                    fontSize: '0.8125rem',
                                    padding: '6px 0',
                                },
                                '& input::placeholder': {
                                    fontSize: '0.8125rem',
                                    opacity: 0.7,
                                },
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <Search fontSize='small' sx={{ fontSize: '1rem' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                )}

                {/* 3-dot Menu Button with Badge */}
                <Badge
                    badgeContent={activeFilterCount}
                    color='primary'
                    max={9}
                    sx={{
                        '& .MuiBadge-badge': {
                            fontSize: '0.65rem',
                            height: 16,
                            minWidth: 16,
                            padding: '0 4px',
                        },
                    }}
                >
                    <IconButton
                        onClick={handleMobileMenuOpen}
                        size='small'
                        sx={{
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.08)'
                                : 'rgba(0, 0, 0, 0.04)',
                            '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.12)'
                                    : 'rgba(0, 0, 0, 0.08)',
                            },
                        }}
                    >
                        <MoreVert fontSize='small' />
                    </IconButton>
                </Badge>

                {/* Mobile Menu */}
                <Menu
                    anchorEl={mobileMenuAnchor}
                    open={mobileMenuOpen}
                    onClose={handleMobileMenuClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        sx: {
                            minWidth: 200,
                            mt: 0.5,
                        }
                    }}
                >
                    {/* Filter Description at top */}
                    {filterDescription && (
                        <Box sx={{
                            px: 2,
                            py: 1.5,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.03)'
                                : 'rgba(0, 0, 0, 0.02)',
                        }}>
                            <Typography
                                variant='caption'
                                sx={{
                                    fontFamily: 'primaryFont',
                                    color: theme.palette.text.secondary,
                                    fontSize: '0.7rem',
                                    fontStyle: 'italic',
                                    display: 'block',
                                    lineHeight: 1.4,
                                }}
                            >
                                {filterDescription}
                            </Typography>
                        </Box>
                    )}

                    {/* Custom Actions (Filters) - rendered inside menu */}
                    {customActions && (
                        <Box sx={{ px: 1.5, py: 1 }}>
                            {customActions}
                        </Box>
                    )}

                    {customActions && (showDenseToggle || showRefresh) && (
                        <Divider />
                    )}

                    {/* Dense Toggle */}
                    {showDenseToggle && onDenseChange && (
                        <MenuItem onClick={handleMobileDenseToggle}>
                            <ListItemIcon>
                                <ViewCompact fontSize='small' />
                            </ListItemIcon>
                            <ListItemText
                                primary='Dense View'
                                secondary={dense ? 'On' : 'Off'}
                                primaryTypographyProps={{ fontSize: '0.875rem' }}
                                secondaryTypographyProps={{ fontSize: '0.75rem' }}
                            />
                            <Switch
                                checked={dense}
                                size='small'
                                color='secondary'
                                onClick={(e) => e.stopPropagation()}
                                onChange={handleDenseChange}
                            />
                        </MenuItem>
                    )}

                    {/* Refresh */}
                    {showRefresh && onRefresh && (
                        <MenuItem onClick={handleMobileRefresh} disabled={isLoading}>
                            <ListItemIcon>
                                {isLoading ? (
                                    <CircularProgress size={18} sx={{ color: theme.palette.primary.main }} />
                                ) : (
                                    <Refresh fontSize='small' />
                                )}
                            </ListItemIcon>
                            <ListItemText
                                primary='Refresh'
                                primaryTypographyProps={{ fontSize: '0.875rem' }}
                            />
                        </MenuItem>
                    )}
                </Menu>
            </Box>
        );
    }

    // Desktop Layout (original)
    return (
        <Box 
            ref={containerRef}
            sx={getToolbarStyles(theme, {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mb: 2,
                px: 1.5,
                py: 1.5,
            })}
        >
            {/* Left Side - Search Bar */}
            <Box 
                ref={leftSideRef}
                sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}
            >
            {search && (
                    <TextField
                        size='small'
                        placeholder={search.placeholder || 'Search...'}
                        value={search.value}
                        onChange={(e) => search.onChange(e.target.value)}
                        sx={{ 
                            minWidth: 250,
                            maxWidth: 400,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                height: 32,
                                fontSize: '0.8125rem',
                                '& input': {
                                    fontSize: '0.8125rem',
                                    padding: '6px 0',
                                },
                                '& input::placeholder': {
                                    fontSize: '0.8125rem',
                                    opacity: 0.7,
                                },
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <Search fontSize='small' sx={{ fontSize: '1rem' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
            )}

                {/* Filters (if provided, placed after search) */}
                {filters.length > 0 && (
                    <>
                        {search && <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />}
                <Box
                    sx={{
                                position: 'relative',
                                flex: 1,
                                minWidth: 0,
                                maxWidth: filterMaxWidth,
                                transition: 'max-width 0.2s ease-in-out',
                            }}
                        >
                            <Box
                                ref={filterContainerRef}
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    alignItems: 'center',
                                    overflowX: 'auto',
                                    overflowY: 'hidden',
                                    width: '100%',
                                    p: 1.5, // Space between chips and scrollbar
                                    '&::-webkit-scrollbar': {
                                        height: 6,
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.2)'
                                            : 'rgba(0, 0, 0, 0.2)',
                                        borderRadius: 3,
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: 'transparent',
                                    },
                                }}
                            >
                                {/* Active filter first */}
                                {organizedFilters.active && renderFilterChip(organizedFilters.active)}
                                
                                {/* Divider after active filter */}
                                {organizedFilters.active && organizedFilters.others.length > 0 && (
                                    <Divider 
                                        orientation="vertical" 
                                        flexItem 
                                        sx={{ 
                                            mx: 0.5,
                                            height: 24,
                                            alignSelf: 'center',
                                            borderColor: theme.palette.divider
                                        }} 
                                    />
                                )}
                                
                                {/* Other filters */}
                                {organizedFilters.others.map((filter) => renderFilterChip(filter))}
                            </Box>
                            {/* Left fade overlay - shows when scrolled from start */}
                            {isScrolledFromStart && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        bottom: 12, // Account for scrollbar space
                                        width: 40,
                                        background: theme.palette.mode === 'dark'
                                            ? 'linear-gradient(to left, transparent, rgba(30, 30, 30, 1))'
                                            : 'linear-gradient(to left, transparent, rgba(255, 255, 255, 1))',
                                        pointerEvents: 'none',
                                        zIndex: 1,
                                    }}
                                />
                            )}
                            {/* Right fade overlay - shows when scrollable */}
                            {isScrollable && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        right: 0,
                                        top: 0,
                                        bottom: 12, // Account for scrollbar space
                                        width: 40,
                                        background: theme.palette.mode === 'dark'
                                            ? 'linear-gradient(to right, transparent, rgba(30, 30, 30, 1))'
                                            : 'linear-gradient(to right, transparent, rgba(255, 255, 255, 1))',
                                        pointerEvents: 'none',
                                        zIndex: 1,
                                    }}
                                />
                            )}
                        </Box>
                    </>
                    )}

                {/* Filter Description - Optional text showing current filter state */}
                {filterDescription && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(0, 0, 0, 0.02)',
                        border: `1px solid ${theme.palette.divider}`,
                    }}>
                        <Typography
                            variant="caption"
                            sx={{
                                fontFamily: 'primaryFont',
                                color: theme.palette.text.secondary,
                                fontSize: '0.75rem',
                                fontStyle: 'italic',
                            }}
                        >
                            {filterDescription}
                        </Typography>
                    </Box>
                )}
                </Box>

            {/* Right Side - Dense & Refresh Controls */}
            <Box 
                ref={rightSideRef}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, ml: 'auto' }}
            >
                    {customActions}

                    {customActions && (showDenseToggle || showRefresh) && (
                    <Divider orientation="vertical" flexItem />
                    )}

                    {showDenseToggle && onDenseChange && (
                            <FormControlLabel
                                label="Dense"
                                control={
                                    <Switch
                                        checked={dense}
                                        onChange={handleDenseChange}
                                        color="secondary"
                                        size="small"
                                    />
                                }
                                sx={{
                                    margin: 0,
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '0.875rem',
                                    }
                                }}
                            />
                    )}

                    {showRefresh && onRefresh && (
                        <IconButton
                            onClick={handleRefreshClick}
                            disabled={isLoading}
                            size="small"
                            sx={{
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.08)'
                                    : 'rgba(0, 0, 0, 0.04)',
                                '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.12)'
                                        : 'rgba(0, 0, 0, 0.08)',
                                },
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress
                                    size={16}
                                    sx={{ color: theme.palette.primary.main }}
                                />
                            ) : (
                                <Refresh fontSize="small" />
                            )}
                        </IconButton>
                    )}
            </Box>
        </Box>
    );
};

export default TableControlBar;

